export type DnsType = {
  base_url?: string;
  created_at?: string;
  type?: string;
  url?: string;
};

export enum ResourceType {
  'CUSTOMERS' = 'CUSTOMERS',
  'CREATE_CUSTOMER' = 'CREATE_CUSTOMER',
  'EDIT_CUSTOMER' = 'EDIT_CUSTOMER',
}

export const enum RBACAccessType {
  'Permanent' = 'Permanent',
  'Temporary' = 'Temporary',
  'Pending' = 'Pending',
  'None' = 'None',
}

export type ProvisioningResponse = {
  status: number;
  data: {
    customers?: CustomerResponseData[];
    cells?: Cells;
  };
  error?: {
    message: string;
    details: Record<string, any>;
  };
};

export type SelectOption<TValue extends string = string> = {
  name: string;
  value: TValue;
};

// ============================================================
//         CUSTOMER TYPES
// ============================================================
export type Customer = {
  organization_name: string;
  organization_name_slug: string;
  cell: string;
  cellKey: string;
  parent_organization_slug?: string;
  parent_organization_slugs: string[];
  api: DnsType;
  app: DnsType;
  sandbox: DnsType;
  is_customer_persisted: boolean;
  description?: string;
  joined: string;
  access_type?: RBACAccessType;
  is_active?: boolean;
  customer_id?: number;
  parent_customer_id?: number;
  is_controlled_child?: boolean;
  /**
   * AFEF - Denotes if a customer is the parent organization of their own cell.
   * Should ONLY be set by Manual DB query.
   */
  is_parent_organization: boolean;
  /** AFEF - Denotes if a customer is a child account to another's parent customer account */
  is_child_organization: boolean;
};

/**
 * the Customer type above was outdated and used in more context than it should
 */
export type CustomerResponseData = {
  app: DnsType;
  cell: string;
  description?: string;
  is_child_organization: boolean;
  is_customer_persisted: boolean;
  is_parent_organization: boolean;
  joined: string;
  organization_name: string;
  organization_name_slug: string;
  parent_organization_slug?: string;
  parent_organization_slugs?: string[];
};

export type CustomerData = CustomerResponseData & {
  cellKey: string;
};

export type CustomerTableData = CustomerData & {
  access_type: string;
  child_access_type?: string;
  customer_id?: number;
  is_active: boolean;
  is_controlled_child?: boolean;
  parent_customer_id?: number;
};

export type CustomerInput = Pick<
Customer,
| 'organization_name'
| 'organization_name_slug'
| 'cell'
| 'parent_organization_slug'
| 'parent_organization_slugs'
| 'description'
> & {
  /** AFEF - Determines if the customer account is expected to be made in an AFEF enabled environment */
  is_afef_child?: boolean;
  is_afef_parent?: boolean;
  /** AFEF - Denotes the type of child cutomer account */
  is_controlled_child: boolean;
};

export type CustomerRbacType = {
  access_type: RBACAccessType;
  customer_id: number;
  is_controlled_child: boolean;
  organization_name: string;
  organization_name_slug: string;
  parent_customer_id: number | null;
};

export type ChildCustomerTypes = 'controlled' | 'autonomous';

// ============================================================
//         CELL TYPES
// ============================================================
export type Cells = Record<string, CellConfig>;

export type CellConfig = {
  /**
   * A Human Readable display name for each cell
   */
  displayName: string;

  /**
   * The ENV type for each defined Cell. Used to filter the list of available cells by matching ENV.
   */
  envType: string;

  /**
   * The URL for the cells API
   */
  apiUrl: string;

  /**
   * The URL for the cells Dashboard App
   */
  appUrl: string;

  /**
   * The URL for the cells Sandbox API
   */
  sandboxUrl: string;
};
